import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createChart, CrosshairMode } from "lightweight-charts";
import { api } from "@/lib/api";

const INTERVALS = [
  { id: "minute5", label: "5d" },
  { id: "minute15", label: "15d" },
  { id: "hour1", label: "1s" },
  { id: "hour4", label: "4s" },
  { id: "day1", label: "1G" },
];

const SIZE_BY_INTERVAL = {
  minute1: 240,
  minute5: 240,
  minute15: 192,
  minute30: 192,
  hour1: 168,
  hour4: 168,
  day1: 120,
};

/**
 * Candlestick chart powered by lightweight-charts.
 * Polls /api/klines/{symbol}?interval=...&size=... every 20s.
 * Optional `markers` prop draws entry/SL/TP horizontal price lines.
 *
 * @param {{ symbol?: string, height?: number, defaultInterval?: string,
 *            priceLines?: Array<{price:number,color?:string,title:string}> }} props
 */
export default function PriceChart({
  symbol = "ETHUSDT",
  height = 360,
  defaultInterval = "minute15",
  priceLines = [],
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const priceLineRefs = useRef([]);
  const [interval, setInterval] = useState(defaultInterval);

  const size = SIZE_BY_INTERVAL[interval] || 96;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["klines", symbol, interval, size],
    queryFn: () => api.klines(symbol, interval, size),
    refetchInterval: 20_000,
    staleTime: 10_000,
  });

  // Init chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      height,
      autoSize: true,
      layout: {
        background: { color: "transparent" },
        textColor: "#9aa4b2",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255,255,255,0.08)",
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const candle = chart.addCandlestickSeries({
      upColor: "#16a34a",
      downColor: "#dc2626",
      borderUpColor: "#16a34a",
      borderDownColor: "#dc2626",
      wickUpColor: "#16a34a",
      wickDownColor: "#dc2626",
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    const vol = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      color: "rgba(120,140,170,0.45)",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candle;
    volumeSeriesRef.current = vol;
    return () => chart.remove();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push data
  useEffect(() => {
    if (!data?.bars || !candleSeriesRef.current) return;
    const bars = data.bars
      .filter((b) => Number.isFinite(b.t))
      .map((b) => ({
        time: b.t,
        open: b.o, high: b.h, low: b.l, close: b.c,
      }));
    const vols = data.bars.map((b) => ({
      time: b.t,
      value: b.v || 0,
      color: b.c >= b.o ? "rgba(22,163,74,0.45)" : "rgba(220,38,38,0.45)",
    }));
    candleSeriesRef.current.setData(bars);
    volumeSeriesRef.current?.setData(vols);
    if (chartRef.current) chartRef.current.timeScale().fitContent();
  }, [data]);

  // Sync horizontal price lines (entry/SL/TP markers)
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    priceLineRefs.current.forEach((pl) => {
      try { series.removePriceLine(pl); } catch (e) { /* ignore */ }
    });
    priceLineRefs.current = [];
    (priceLines || []).forEach((pl) => {
      if (!Number.isFinite(pl.price)) return;
      const ref = series.createPriceLine({
        price: pl.price,
        color: pl.color || "#3b82f6",
        lineWidth: 1,
        lineStyle: 2, // dashed
        axisLabelVisible: true,
        title: pl.title || "",
      });
      priceLineRefs.current.push(ref);
    });
  }, [priceLines]);

  const lastClose = useMemo(() => {
    const arr = data?.bars;
    return arr && arr.length ? arr[arr.length - 1].c : null;
  }, [data]);

  return (
    <div className="rounded-lg border border-white/5 bg-black/30" data-testid="price-chart">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-slate-400">
            Grafik
          </span>
          <span className="font-mono text-sm text-slate-200" data-testid="price-chart-symbol">
            {symbol}
          </span>
          {lastClose !== null && (
            <span className="font-mono text-xs text-slate-400" data-testid="price-chart-last">
              ${lastClose.toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex gap-1" role="tablist">
          {INTERVALS.map((iv) => (
            <button
              key={iv.id}
              data-testid={`chart-tf-${iv.id}`}
              onClick={() => setInterval(iv.id)}
              className={
                "px-2 py-0.5 text-[11px] font-mono rounded transition " +
                (interval === iv.id
                  ? "bg-emerald-500/20 text-emerald-200"
                  : "text-slate-400 hover:text-slate-200")
              }
            >
              {iv.label}
            </button>
          ))}
        </div>
      </div>
      <div className="relative" style={{ height }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-500">
            <span data-testid="price-chart-loading">yükleniyor…</span>
          </div>
        )}
        {isError && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-rose-400">
            grafik alınamadı
          </div>
        )}
        <div ref={containerRef} className="w-full h-full" />
      </div>
    </div>
  );
}
